/**
 * Sparkline — tiny inline SVG chart for KPI cards.
 *
 * `data` is an array of numbers (any length). Sparkline auto-fits and
 * highlights the last point. If `data` is empty or all-zero we render
 * a subtle dashed baseline so the card doesn't feel broken.
 */
import { useId } from "react";

export default function Sparkline({
  data = [],
  color = "#F59E0B",
  height = 32,
  width = 96,
  showArea = true,
  showDot = true,
  strokeWidth = 1.5,
}) {
  const gid = useId().replace(/:/g, "-");
  const clean = data.filter((n) => Number.isFinite(n));
  const empty = clean.length < 2 || clean.every((n) => n === clean[0]);

  if (empty) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
           preserveAspectRatio="none" aria-hidden="true">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2}
              stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
      </svg>
    );
  }

  const max = Math.max(...clean);
  const min = Math.min(...clean);
  const step = width / (clean.length - 1);
  const pad = 2;
  const yFor = (v) => {
    const range = Math.max(1e-6, max - min);
    return height - pad - ((v - min) / range) * (height - pad * 2);
  };
  const points = clean.map((v, i) => [i * step, yFor(v)]);
  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${path} L ${width} ${height} L 0 ${height} Z`;
  const [lx, ly] = points[points.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={`spark-${gid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.28" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {showArea && <path d={areaPath} fill={`url(#spark-${gid})`} />}
      <path d={path} stroke={color} strokeWidth={strokeWidth}
            fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {showDot && (
        <circle cx={lx} cy={ly} r="2.2" fill="white"
                stroke={color} strokeWidth="1.5" />
      )}
    </svg>
  );
}

/**
 * Given a "target" numeric value, generate a deterministic 7-point
 * sparkline series that lands on it — used so mock trends stay stable
 * across re-renders without needing a real time-series API yet.
 */
export function seedSeries(target, seedStr = "v3", points = 7) {
  const t = Number(target) || 0;
  let seed = 0;
  for (let i = 0; i < String(seedStr).length; i++) {
    seed = (seed * 31 + String(seedStr).charCodeAt(i)) & 0xffff;
  }
  const rand = () => {
    seed = (seed * 9301 + 49297) & 0xffff;
    return (seed % 1000) / 1000;
  };
  // Trend up: start ~85% of target, end at target with some noise.
  const start = t * (0.78 + rand() * 0.12);
  const out = [];
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    const linear = start + (t - start) * progress;
    const noise = (rand() - 0.5) * t * 0.08;
    out.push(Math.max(0, linear + noise));
  }
  out[out.length - 1] = t; // land exactly on target
  return out;
}
